// Pure helpers for the estimate map panel: shape/marker types, the persisted
// mapJson format, and the derived area math. Kept out of the component so the
// saved-data parsing and the net-area calculation can be unit tested.

export interface MapShape {
  id: string
  type: 'polygon' | 'line' | 'subtract'
  coords: [number, number][]
  label: string
  measurement: number // always stored in SF or LF
  color: string
}

// A dropped location pin/note (e.g. a pothole). No measurement — marker only.
export interface MapMarker {
  id: string
  coord: [number, number] // lng, lat
  label: string
  note: string
  color: string
}

export interface MapViewport {
  center: [number, number]
  zoom: number
}

export interface ParsedMapData {
  shapes: MapShape[]
  markers: MapMarker[]
  viewport: MapViewport | undefined
}

// Color used to render "subtract" (cut-out) areas so they read as removals.
export const SUBTRACT_COLOR = '#EF4444'

// Amber reads as a "point of interest / issue" pin.
export const MARKER_COLOR = '#F59E0B'

// Palette offered by the per-shape color picker. Deliberately excludes red —
// red is reserved for subtract cut-outs, and a red area is indistinguishable
// from a cut-out at a glance (they differ only by the dashed outline).
export const SHAPE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#84CC16']

/**
 * Parse the persisted `mapJson` value.
 *
 * Purely additive: every historical shape of this field still loads.
 *   - `{ shapes, markers, viewport }` — current format
 *   - `{ shapes, viewport }`          — pre-markers format (markers default to [])
 *   - `[ ...shapes ]`                 — legacy array-only format
 *   - a JSON string of any of the above
 * Anything unrecognized or malformed yields empty data rather than throwing,
 * so a bad value can never take the map panel down.
 */
export function parseMapData(initialMapData: unknown): ParsedMapData {
  const empty: ParsedMapData = { shapes: [], markers: [], viewport: undefined }
  if (!initialMapData) return empty

  let data: any = initialMapData
  if (typeof data === 'string') {
    try { data = JSON.parse(data) } catch { return empty }
  }

  // Current / pre-markers object format
  if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.shapes)) {
    return {
      shapes: data.shapes as MapShape[],
      markers: (Array.isArray(data.markers) ? data.markers : []) as MapMarker[],
      viewport: (data.viewport ?? undefined) as MapViewport | undefined,
    }
  }

  // Legacy format: just an array of shapes
  if (Array.isArray(data)) return { ...empty, shapes: data as MapShape[] }

  return empty
}

/** Gross measured area in SF — added polygons only, before cut-outs. */
export function grossPolygonSF(shapes: MapShape[]): number {
  return shapes.reduce((sum, s) => (s.type === 'polygon' ? sum + s.measurement : sum), 0)
}

/** Total SF removed by subtract (cut-out) shapes. */
export function subtractedSF(shapes: MapShape[]): number {
  return shapes.reduce((sum, s) => (s.type === 'subtract' ? sum + s.measurement : sum), 0)
}

/**
 * Net measured area in SF: added polygons minus subtracted cut-outs.
 *
 * This is arithmetic, not geometric — overlapping cut-outs subtract twice, and
 * a cut-out drawn outside every area still subtracts. Clamped at 0 so a
 * mis-drawn cut-out can never persist a negative square footage.
 */
export function netPolygonSF(shapes: MapShape[]): number {
  return Math.max(0, grossPolygonSF(shapes) - subtractedSF(shapes))
}

/** Total line length in LF. */
export function totalLineLF(shapes: MapShape[]): number {
  return shapes.reduce((sum, s) => (s.type === 'line' ? sum + s.measurement : sum), 0)
}

/**
 * Next auto-label for a family (e.g. "Area 3").
 *
 * Uses the highest number already in use rather than the item count, so
 * deleting "Area 1" of two doesn't mint a second "Area 2".
 */
export function nextLabel(existing: string[], prefix: string): string {
  const pattern = new RegExp(`^${prefix} (\\d+)$`)
  const highest = existing.reduce((max, label) => {
    const m = pattern.exec(label)
    return m ? Math.max(max, Number(m[1])) : max
  }, 0)
  return `${prefix} ${highest + 1}`
}

/** Collision-free id for a new shape or marker. */
export function newId(prefix: string): string {
  const uuid = globalThis.crypto?.randomUUID?.()
  return `${prefix}-${uuid ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`}`
}

/** Escape user text before injecting it into marker/popup innerHTML. */
export function escapeHtml(str: string): string {
  return String(str).replace(/[&<>"']/g, ch => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string
  ))
}
